import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@hero/web-lib';
import { Skeleton } from '../../../ui/Skeleton';

export interface PricingSheetSkeletonProps {
  /** Number of skeleton rows to render. Defaults to 6 so the layout feels populated. */
  rows?: number;
  className?: string;
}

/**
 * Loading state for the pricing sheet. Mirrors the column layout of
 * `PricingSheetTable` (origin · destination · vehicles · trip · load · rate
 * · status · actions) so when data lands the skeleton rows swap into real
 * rows with zero layout shift.
 *
 * Used by `PricingSheetPage` while React Query is fetching the initial
 * route list — the empty state was previously rendered in this window,
 * which made the page look "empty then empty again" once data arrived.
 */
export function PricingSheetSkeleton({
  rows = 6,
  className,
}: PricingSheetSkeletonProps) {
  const { t } = useTranslation(['m-pricing']);

  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm',
        className,
      )}
      aria-busy
      aria-live="polite">
      <table className="min-w-full text-xs">
        <thead>
          <tr className="border-b border-gray-200 bg-gray-50/60">
            <th className="w-[40px] px-3 py-2">
              <Skeleton className="size-4" />
            </th>
            <SkHeader>{t('sheet.cols.origin', { defaultValue: 'Origin' })}</SkHeader>
            <th aria-hidden className="w-[24px] px-0" />
            <SkHeader>
              {t('sheet.cols.destination', { defaultValue: 'Destination' })}
            </SkHeader>
            <SkHeader>
              {t('sheet.cols.vehicles', { defaultValue: 'Vehicles' })}
            </SkHeader>
            <SkHeader>{t('sheet.cols.trip', { defaultValue: 'Trip' })}</SkHeader>
            <SkHeader>{t('sheet.cols.load', { defaultValue: 'Load' })}</SkHeader>
            <SkHeader align="end">
              {t('sheet.cols.price', { defaultValue: 'Rate' })}
            </SkHeader>
            <SkHeader>{t('sheet.cols.status', { defaultValue: 'Status' })}</SkHeader>
            <th aria-hidden className="w-[72px]" />
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <tr key={i} className="border-b border-gray-100">
              <td className="px-3 py-2 align-middle">
                <Skeleton className="size-4" />
              </td>
              <SkCell width="w-[120px]" />
              <td aria-hidden className="w-[24px] px-0" />
              <SkCell width="w-[120px]" />
              <SkCell width="w-[160px]" />
              <SkCell width="w-[90px]" />
              <SkCell width="w-[90px]" />
              <SkCell width="w-[80px]" align="end" />
              <SkCell width="w-[60px]" />
              <td className="w-[72px] px-1.5 py-2" />
            </tr>
          ))}
        </tbody>
      </table>
      <span className="sr-only">
        {t('sheet.loading', { defaultValue: 'Loading rates…' })}
      </span>
    </div>
  );
}

function SkHeader({
  children,
  align = 'start',
}: {
  children: React.ReactNode;
  align?: 'start' | 'end';
}) {
  return (
    <th
      className={cn(
        'px-1.5 py-2 text-[10px] font-medium uppercase tracking-wider text-gray-400',
        align === 'end' ? 'text-end' : 'text-start',
      )}>
      {children}
    </th>
  );
}

function SkCell({
  width,
  align = 'start',
}: {
  width: string;
  align?: 'start' | 'end';
}) {
  return (
    <td
      className={cn(
        'px-1.5 py-2 align-middle',
        align === 'end' ? 'text-end' : 'text-start',
      )}>
      <Skeleton className={cn('h-7 rounded-md', width, align === 'end' && 'ms-auto')} />
    </td>
  );
}
